import { FC, useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { PropertyTable } from '@components/properties/PropertyTable';
import { PropertyFilterBar, ViewMode } from '@components/properties/PropertyFilterBar';
import { PropertyCard } from '@components/properties/PropertyCard';
import { PropertyFormWizard } from '@components/properties/PropertyFormWizard';
import { Modal } from '@components/feedback/Modal';
import { Button } from '@components/common/Button';
import { Property, PropertyStatus, PortfolioPropertyFormData } from '@/types/index';
import { propertyService } from '@services/property.service';

const PropertyMap = lazy(() => import('@components/properties/PropertyMap').then(m => ({ default: m.PropertyMap })));

const STORAGE_KEY = 'immohelvet_opportunities_view_mode';

export const Opportunities: FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(STORAGE_KEY) as ViewMode | null) ?? 'cards'
  );
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(
    undefined,
  );

  const [showOffMarket, setShowOffMarket] = useState(false);

  useEffect(() => {
    loadProperties();
  }, [showOffMarket]);

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      const data = await propertyService.getAllFiltered(
        showOffMarket ? { isOffMarket: true } : undefined
      );
      setProperties(data.properties);
    } catch (error) {
      console.error('Erreur lors du chargement des opportunités:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProperty = () => {
    setSelectedProperty(undefined);
    setIsWizardOpen(true);
  };

  const handleEditProperty = async (property: Property) => {
    try {
      const freshProperty = await propertyService.getById(property.id);
      setSelectedProperty(freshProperty);
    } catch (error) {
      console.error('Erreur chargement opportunité:', error);
      setSelectedProperty(property);
    }
    setIsWizardOpen(true);
  };

  const handleDeleteProperty = async (id: string) => {
    try {
      await propertyService.delete(id);
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de l\'opportunité');
    }
  };

  const handleWizardSuccess = () => {
    setIsWizardOpen(false);
    setSelectedProperty(undefined);
    loadProperties();
  };

  const handleWizardCancel = () => {
    setIsWizardOpen(false);
    setSelectedProperty(undefined);
    loadProperties();
  };

  const handleAddToPortfolio = (property: Property) => {
    const prefill: PortfolioPropertyFormData = {
      name: property.address ? `${property.address}, ${property.city}` : property.city,
      propertyId: property.id,
      address: property.address || undefined,
      city: property.city,
      postalCode: property.postalCode,
      canton: property.canton,
      area: property.area,
      rooms: property.rooms,
      acquisitionPrice: property.askingPrice,
      grossAnnualRent: property.isRented && property.rentPrice
        ? property.rentPrice * 12
        : property.estimatedRent
          ? property.estimatedRent * 12
          : undefined,
      ppeChargesAnnual: property.ppeCharges ? property.ppeCharges * 12 : undefined,
    };
    navigate('/portfolio', { state: { prefillFromProperty: prefill } });
  };

  const handleSimulate = (property: Property) => {
    navigate(`/analysis/${property.id}`, { state: { openTab: 'simulations' } });
  };

  const handleRejectProperty = async (property: Property) => {
    try {
      await propertyService.update(property.id, { status: PropertyStatus.REJECTED });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      alert('Erreur lors du rejet de la propriété');
    }
  };

  const handlePromoteProperty = async (property: Property) => {
    try {
      const newStatus = property.status === PropertyStatus.OPPORTUNITY
        ? PropertyStatus.FIRST_SELECTION
        : PropertyStatus.OPPORTUNITY;
      await propertyService.update(property.id, { status: newStatus });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la promotion:', error);
    }
  };

  return (
    <DashboardLayout title="Opportunités">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Opportunités
          </h1>
          <Button onClick={handleAddProperty} variant="primary">
            + Ajouter une opportunité
          </Button>
        </div>

        {/* Filtres */}
        <PropertyFilterBar
          properties={properties}
          onFilterChange={setFilteredProperties}
          defaultStatuses={[PropertyStatus.OPPORTUNITY]}
          showOffMarket={showOffMarket}
          onToggleOffMarket={() => setShowOffMarket(v => !v)}
          viewMode={viewMode}
          onViewModeChange={(m) => { setViewMode(m); localStorage.setItem(STORAGE_KEY, m); }}
        />

        {/* Vue tableau */}
        {viewMode === 'table' && (
          <PropertyTable
            properties={filteredProperties}
            onEdit={handleEditProperty}
            onDelete={handleDeleteProperty}
            onReject={handleRejectProperty}
            onAddToPortfolio={handleAddToPortfolio}
            onSimulate={handleSimulate}
            isLoading={isLoading}
          />
        )}

        {/* Vue cards */}
        {viewMode === 'cards' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-32 bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />
                ))
              : filteredProperties.map(p => (
                  <PropertyCard
                    key={p.id}
                    property={p}
                    onEdit={handleEditProperty}
                    onReject={handleRejectProperty}
                    onPromote={handlePromoteProperty}
                  />
                ))
            }
          </div>
        )}

        {/* Vue carte */}
        {viewMode === 'map' && (
          <Suspense fallback={<div className="h-[600px] bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />}>
            <PropertyMap properties={filteredProperties} />
          </Suspense>
        )}

        {/* Modal Wizard */}
        <Modal
          isOpen={isWizardOpen}
          onClose={handleWizardCancel}
          title={
            selectedProperty ? 'Modifier l\'opportunité' : 'Ajouter une opportunité'
          }
          size="xl"
        >
          <PropertyFormWizard
            property={selectedProperty}
            onSuccess={handleWizardSuccess}
            onCancel={handleWizardCancel}
            onSave={loadProperties}
          />
        </Modal>
      </div>
    </DashboardLayout>
  );
};
